import React, { useMemo } from 'react';

import { connect } from 'react-redux';
import type {
	ExtensionPointModule,
	WithExtensionListProps,
	WithBlockedAppsBannerProps,
	ForgePageName,
} from '@helpcenter/forge-ui-utils';
import {
	RESOURCE_TYPE_ISSUE,
	RESOURCE_TYPE_PROJECT,
	RESOURCE_TYPE_UNKNOWN,
} from '@helpcenter/forge-ui-utils/src/constants';
import { mapDispatchToPropsBanner } from 'state/persisted/blocked-apps-banner/reducer';
import { useTFExtensionList } from '../use-tf-extension-list';

const TrustedFetchExtensionComponent: React.FC<
	React.PropsWithChildren<
		{
			extensionType: ExtensionPointModule;
			resourceId: string | undefined;
			resourceType?: string;
			page?: ForgePageName;
		} & WithBlockedAppsBannerProps
	>
> = ({ extensionType, page, children, resourceId, resourceType, showBanner, resetBannerState }) => {
	const resourceContext = useMemo(
		() => ({
			resourceId,
			resourceType,
		}),
		[resourceId, resourceType],
	);

	const { extensions } = useTFExtensionList({
		resourceContext,
		page,
		type: extensionType,
	});

	const childrenWithProps = React.Children.only(
		React.cloneElement(
			children as React.ReactElement<WithExtensionListProps & WithBlockedAppsBannerProps>,
			{
				extensions,
				showBanner,
				resetBannerState,
			},
		),
	);

	return <>{childrenWithProps}</>;
};

const TrustedFetchComponentsWithBannerProps = connect(
	null,
	mapDispatchToPropsBanner,
)(TrustedFetchExtensionComponent);

export const withExtensionList =
	(type?: ExtensionPointModule) =>
	<TProps extends WithExtensionListProps>(
		WrappedComponent: React.FC<TProps>,
	): React.FC<Omit<TProps, 'extensions'>> =>
	// upgrading eslint-plugin-react to 7.31.11. Please correct when this code is revisited.
	// eslint-disable-next-line react/display-name
	(props: TProps) => {
		const extensionType = type || props?.type;

		if (!extensionType) {
			return <WrappedComponent {...props} extensions={[]} />;
		}
		const { requestKey, projectId, page } = props;
		let resourceType = RESOURCE_TYPE_UNKNOWN;
		let resourceId;
		if (requestKey) {
			resourceType = RESOURCE_TYPE_ISSUE;
			resourceId = requestKey;
		} else if (projectId) {
			resourceType = RESOURCE_TYPE_PROJECT;
			resourceId = projectId.toString();
		}

		return (
			<TrustedFetchComponentsWithBannerProps
				extensionType={extensionType}
				resourceId={resourceId}
				resourceType={resourceType}
				page={page}
			>
				<WrappedComponent {...props} />
			</TrustedFetchComponentsWithBannerProps>
		);
	};
